import { useState, useEffect, useCallback, useRef } from 'react';
import {
  bundleShaMatches,
  fetchRemoteVersion,
  getWebBundleMeta,
  isPlaybackStoppedForReload,
  reloadWebBundle,
} from '../utils/webBundleMeta';

const CHECK_DEBOUNCE_MS = 2000;

/**
 * On launch and window focus, compare embedded bundle SHA to live /version.json.
 * Shows stale=true for banner UI; silently reloads when playback is stopped.
 */
export function useWebBundleStaleness(): { stale: boolean; refresh: () => void } {
  const [stale, setStale] = useState(false);
  const checkingRef = useRef(false);
  const lastCheckRef = useRef(0);
  const embedded = getWebBundleMeta();

  const check = useCallback(async () => {
    const now = Date.now();
    if (checkingRef.current || now - lastCheckRef.current < CHECK_DEBOUNCE_MS) return;
    checkingRef.current = true;
    lastCheckRef.current = now;

    try {
      const remote = await fetchRemoteVersion();
      if (!remote?.sha && !remote?.gitHash) return;

      const remoteSha = remote.sha || remote.gitHash || '';
      if (bundleShaMatches(embedded.sha, remoteSha)) {
        setStale(false);
        return;
      }

      if (isPlaybackStoppedForReload()) {
        reloadWebBundle();
        return;
      }

      setStale(true);
    } finally {
      checkingRef.current = false;
    }
  }, [embedded.sha]);

  const refresh = useCallback(() => {
    reloadWebBundle();
  }, []);

  useEffect(() => {
    void check();

    const onFocus = () => void check();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [check]);

  return { stale, refresh };
}
